/**
 * Chat widget configuration interface
 */
export interface ChatWidget {
  /** Unique identifier for the widget (backend-generated integer) */
  id: number;

  /** Display name for the widget */
  name: string;

  /** Whether the widget is enabled */
  enabled: boolean;

  /** Monday.com account/workspace ID */
  accountId?: string;

  /** Monday.com board ID this widget is mapped to */
  boardId?: string;

  /** Monday.com board name */
  boardName?: string;

  /** Backend configuration ID */
  configurationId?: string;

  /** Creation timestamp */
  createdAt: string;
}

/**
 * Twilio integration configuration interface
 */
export interface TwilioIntegration {
  /** Unique identifier for the integration */
  id: string;

  /** Integration type: SMS or WhatsApp */
  type: 'sms' | 'whatsapp';

  /** Provider name */
  provider: 'twilio';

  /** Twilio Account SID */
  accountSid: string;

  /** Twilio Auth Token */
  authToken: string;

  /** Twilio phone number */
  phoneNumber: string;

  /** Monday.com account/workspace ID */
  accountId?: string;

  /** Monday.com board ID for conversations */
  boardId?: string;

  /** Monday.com board name */
  boardName?: string;

  /** Backend configuration ID */
  configurationId?: string;

  /** Whether the integration is active */
  active: boolean;

  /** Creation timestamp */
  createdAt: string;
}

/**
 * Settings configuration interface for Monday.com app
 */
export interface AppSettings {
  /** API key or token for external integrations */
  apiKey: string;

  /** Enable/disable feature flags */
  featureFlags: {
    enableNotifications: boolean;
    enableAutoSync: boolean;
    enableAdvancedMode: boolean;
  };

  /** User preferences */
  preferences: {
    language: string;
    timezone: string;
    dateFormat: string;
  };

  /** Integration-specific settings */
  integration: {
    webhookUrl?: string;
    syncInterval: number;
  };

  /** Chat widgets configuration */
  chatWidgets: ChatWidget[];

  /** Twilio integrations configuration */
  twilioIntegrations: TwilioIntegration[];
}

/**
 * Default settings configuration
 */
export const DEFAULT_SETTINGS: AppSettings = {
  apiKey: '',
  featureFlags: {
    enableNotifications: true,
    enableAutoSync: false,
    enableAdvancedMode: false,
  },
  preferences: {
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
  },
  integration: {
    webhookUrl: '',
    syncInterval: 300, // 5 minutes in seconds
  },
  chatWidgets: [],
  twilioIntegrations: [],
};
